import { useParams } from 'wouter';
import { AdminOrganizations } from '@platform/client';
import { OrganizationApplicationStatusEnum, ReviewOrgData } from '@platform/client/schemas';
import { useState } from 'react';
import {
  ReviewPageWrapper,
  ReviewItem,
  ReviewItemShort,
  AdminViewPageAction,
} from '../../AdminViewPage';
import { UpdateAppStatusModal } from './UpdateApplicationStatusModal';
import { getOrgAppStatusBadgeColor } from './OrganizationsAdmin';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';

export const OrganizationReview = () => {
  const { organizationId } = useParams();
  const {
    data: organization,
    isLoading,
    isError,
    isSuccess,
  } = AdminOrganizations.useGetReviewData(Number(organizationId));
  const [updateAppStatusOrg, setUpdateAppStatusOrg] = useState<ReviewOrgData>();

  if (!organization) {
    return null;
  }
  const pageActions: AdminViewPageAction[] = [
    {
      type: 'link',
      text: 'Edit',
      href: `${import.meta.env['VITE_LEGACY_APP_URL']}/admin/organization/${organizationId}/edit`,
    },
  ];
  if (organization.applicationStatus) {
    pageActions.push({
      type: 'button',
      text: 'Update application status',
      onPress: () => setUpdateAppStatusOrg(organization),
    });
  }

  return (
    <ReviewPageWrapper
      title={organization.name}
      badge={{
        text: organization.applicationStatus?.split('_').join(' ').toUpperCase() ?? 'N/A',
        color:
          organization.applicationStatus ?
            getOrgAppStatusBadgeColor(organization.applicationStatus)
          : 'gray',
      }}
      headerIconItems={[
        {
          icon: 'calendar-check-01',
          label: 'Registration date',
          value: organization.registrationDate,
        },
        { icon: 'marker-pin-01', label: 'Registration country', value: organization.country },
        { icon: 'building-06', label: 'Organization type', value: organization.type },
        { icon: 'users-02', label: 'Organization size', value: organization.size },
      ]}
      headerLinks={[
        { label: 'Website', url: organization.website },
        { label: 'LinkedIn', url: organization.linkedin },
      ]}
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      backNavigation={{
        label: 'Back to organizations',
        href: `~platform${ROUTES.ADMIN}${ADMIN_ROUTES.ORGANIZATIONS}`,
      }}
      pageActions={pageActions}
    >
      <GeneratingRevenueDetail organization={organization} />
      {organization.impactTracking && (
        <ReviewItemShort
          label='Impact Tracking'
          value={organization.impactTracking.replaceAll('-', ' ')}
        />
      )}
      <ReviewItemShort label='Corporate Client' value={organization.corporateClient} />
      <ReviewItemShort label='Impact Partners' value={organization.partners.join(', ')} />
      <ReviewItemShort label='Industries' value={organization.industries.join(', ')} />
      <ReviewItemShort label='Causes' value={organization.causes.join(', ')} />
      {organization.description && (
        <ReviewItem label='Description'>
          <p dangerouslySetInnerHTML={{ __html: organization.description }} />
        </ReviewItem>
      )}
      {organization.impactStory && (
        <ReviewItem label='Impact Story'>
          <p dangerouslySetInnerHTML={{ __html: organization.impactStory }} />
        </ReviewItem>
      )}
      {organization.businessProblem && (
        <ReviewItem label='Business Problem'>
          <p>{organization.businessProblem}</p>
        </ReviewItem>
      )}
      {organization.businessSolution && (
        <ReviewItem label='Business Solution'>
          <p>{organization.businessSolution}</p>
        </ReviewItem>
      )}
      {organization.challengeOne && (
        <BusinessChallenge
          label='Challenge One'
          challenge={organization.challengeOne}
          context={organization.contextOne}
        />
      )}
      {organization.challengeTwo && (
        <BusinessChallenge
          label='Challenge Two'
          challenge={organization.challengeTwo}
          context={organization.contextTwo}
        />
      )}
      {organization.challengeThree && (
        <BusinessChallenge
          label='Challenge Three'
          challenge={organization.challengeThree}
          context={organization.contextThree}
        />
      )}
      {updateAppStatusOrg && (
        <UpdateAppStatusModal
          onClose={() => setUpdateAppStatusOrg(undefined)}
          organizationId={updateAppStatusOrg.organizationId}
          status={updateAppStatusOrg.applicationStatus as OrganizationApplicationStatusEnum}
          applicationId={updateAppStatusOrg.applicationId as number}
        />
      )}
    </ReviewPageWrapper>
  );
};

const GeneratingRevenueDetail = ({ organization }: { organization: ReviewOrgData }) => {
  const amountText = organization.revenueAmount ?? 'No amount provided';
  const answerText =
    organization.generatingRevenue !== undefined ?
      organization.generatingRevenue ?
        'Yes'
      : 'Not Generating Revenue'
    : 'Not Answered';
  const valueText = organization.generatingRevenue ? `${answerText} (${amountText})` : answerText;
  return <ReviewItemShort label='Generating Revenue' value={valueText} />;
};

const BusinessChallenge = ({
  label,
  challenge,
  context,
}: {
  label: string;
  challenge: string;
  context?: string | null;
}) => {
  return (
    <div className='flex flex-col gap-1'>
      <ReviewItemShort label={label} value={challenge.replaceAll('-', ' ')} />
      {context && <p>{context}</p>}
    </div>
  );
};
